import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';

const endpoint = (instanceUniqueId) =>
  `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${encodeURIComponent(instanceUniqueId)}/capabilities`;

export default function EzeeCapabilityPanel({ instanceUniqueId }) {
  const [capabilities, setCapabilities] = useState([]);
  const [deployment, setDeployment] = useState({ runtimeArmed: false, backgroundWorkersArmed: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetch(endpoint(instanceUniqueId), {
      headers: { accessToken: localStorage.getItem('accessToken') },
    }).then(async res => {
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.message || 'Unable to read Ezee switches.');
      if (!cancelled) {
        setCapabilities(body.capabilities || []);
        setDeployment(body.deployment || { runtimeArmed: false, backgroundWorkersArmed: false });
      }
    }).catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [instanceUniqueId]);

  async function change(key, value) {
    setSaving(key);
    setError('');
    setMessage('');
    try {
      const response = await fetch(endpoint(instanceUniqueId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', accessToken: localStorage.getItem('accessToken') },
        body: JSON.stringify({ capabilities: { [key]: value } }),
      });
      const body = await response.json();
      if (!response.ok || !body.success) throw new Error(body.message || 'Failed to update Ezee capability.');
      setCapabilities(body.capabilities || []);
      if (body.deployment) setDeployment(body.deployment);
      setMessage('Capability settings saved. Server-side restrictions apply immediately.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  }
  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <Typography variant="h6" fontWeight={700}>Ezee capability breakers</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each breaker starts off. The Nervous System master switch and emergency stop override every capability.
        Existing Email Mode, Action Engine and autonomy safeguards continue to apply.
      </Typography>
      {!loading && !deployment.runtimeArmed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ezee is installed in cold-start mode. No AI capability can operate or be switched ON
          until an authorized developer explicitly arms the Ezee runtime. You can safely inspect
          or turn OFF existing switches here.
        </Alert>
      )}
      {!loading && deployment.runtimeArmed && !deployment.backgroundWorkersArmed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Manual commissioning only: all Ezee background workers remain disabled until separately authorized.
        </Alert>
      )}
      {loading && <CircularProgress size={22} />}
      {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ my: 1 }}>{message}</Alert>}
      <Stack spacing={1} divider={null}>
        {capabilities.map(capability => (
          <Box key={capability.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box>
              <Typography variant="body1" fontWeight={600}>{capability.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {capability.allowed ? 'Operational' : capability.reason === 'runtime_disabled' ? 'Deployment runtime is disarmed' : capability.reason === 'master_disabled' ? 'Master switch is off or emergency-stopped' : capability.reason === 'dependency_disabled' ? `Requires ${capability.dependency}` : 'Disabled'}
              </Typography>
            </Box>
            <FormControlLabel sx={{ mr: 0 }} label={capability.enabled ? 'On' : 'Off'} labelPlacement="start"
              control={<Switch checked={capability.enabled} disabled={Boolean(saving) || (!deployment.runtimeArmed && !capability.enabled)}
                onChange={event => change(capability.key, event.target.checked)} />}
            />
          </Box>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        A green operational state requires the master switch, this breaker, and all dependencies to be enabled.
        Changes affect future execution; they do not undo completed actions.
      </Typography>
    </Paper>
  );
}
